import { Form, FormInstance, Row, Tabs, TabsProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { Details } from "./Details";
import { PaymentHistory } from "./PaymentHistory";
import { Legal } from "./Legal";
import { Documents } from "./Documents";
import { Ticklers } from "./Ticklers";
import { Payoff } from "./Payoff";
import { useFetchAccount } from "./Details/hooks/fetchAccount";
import { useLeaveDetailsView } from "../../../hooks/leaveDetailsView";
import { useUpdateAccount } from "../../../hooks/updateAccount";
import { AccountHeader } from "../../AccountHeader";
import { EditPanel } from "./Details/components/EditPanel";
import { LeaveModal } from "../../../../../components/LeaveModal";
import dayjs from "../../../../../configs/dayjs";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../../store";
import { setLocation } from "../../../../../acions/actionsCreators/nextLocation";
import { useHistoryBackStep } from "../../../../../lib/hooks/useHistoryBackStep";
import { useSetLastUpdated } from "./Details/hooks/setLastUpdated";
import ROUTES from "../../../../../configs/routesConfig";
import { Mailings } from "./Mailings";
import { HeaderActions } from "./HeaderActions";
import { useSelector } from "react-redux";
import { IncidentReports } from "../../../../Customer/components/Tabs/IncidentReports";
import { Loader } from "../../../../../components/Loader";

type AdminViewProps = {
  contractId: string;
};

export const AdminView = ({ contractId }: AdminViewProps) => {
  const [form]: FormInstance[] = Form.useForm();

  const mailingKey = useSelector((state: any) => state.mailingKey.key);

  const navigate: NavigateFunction = useNavigate();

  const { backLocation, reduceBackStep } = useHistoryBackStep(ROUTES.ACCOUNTS);

  const dispatch = useAppDispatch();

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [active, setActive] = useState<string>("1");

  const [disbursementsSidebarIsOpen, setDisbursementsSidebarIsOpen] = useState<boolean>(false);

  const [impoundsSidebarIsOpen, setImpoundsSidebarIsOpen] = useState<boolean>(false);

  const [ticklersSidebarIsOpen, setTicklersSidebarIsOpen] = useState<boolean>(false);

  const [feesSidebarIsOpen, setFeesSidebarIsOpen] = useState<boolean>(false);

  const { data, isLoading, setData, fetchData, legalData } = useFetchAccount(contractId, form);

  const contractNumber: number | undefined = useMemo(() => (data ? data.accountNumber : undefined), [data]);

  const { lastUpdated } = useSetLastUpdated(data);

  const reloadAccount = useCallback((): void => {
    fetchData();
    setHasChanges(false);
  }, [fetchData]);

  const { onLeaveView, isFormDisabled, setIsFormDisabled } = useLeaveDetailsView(hasChanges);

  const { update, lcSectionKey } = useUpdateAccount(form, contractId, setIsFormDisabled);

  const updateAndReset = useCallback(() => {
    update((): void => {
      setData((prev: any) => ({
        ...prev,
        ...form.getFieldsValue(),
        lastModifiedDateTime: dayjs().format()
      }));
      setHasChanges(false);
    });
  }, [form, setData, update]);

  const onCancelChanges = useCallback((): void => {
    form.resetFields();
    form.setFields([
      {
        name: "bankAccountNumber",
        errors: []
      }
    ]);
    setHasChanges(false);
  }, [form]);

  const onBackButtonClicked = useCallback((): void => {
    hasChanges && dispatch(setLocation(backLocation));

    navigate(backLocation);
  }, [backLocation, dispatch, hasChanges, navigate]);

  const onChangeTab = useCallback(
    (tabkey: string): void => {
      if (active !== "1") {
        setActive(tabkey);
      } else {
        const onSuccess = () => {
          setActive(tabkey);
          setHasChanges(false);
        };

        const onCancel = () => {
          onSuccess();
          onCancelChanges();
        };

        onLeaveView(() => update(onSuccess), onCancel);
      }
    },
    [active, onLeaveView, onCancelChanges, update]
  );

  const tabItems: TabsProps["items"] = useMemo(() => {
    return [
      {
        key: "1",
        label: "Account Details",
        children: (
          <Details
            id={contractId}
            form={form}
            data={data}
            isLoading={isLoading}
            isDisabled={isFormDisabled}
            setHasChanges={setHasChanges}
            reloadAccount={reloadAccount}
            lcSectionKey={lcSectionKey}
            disbursementsSidebarIsOpen={disbursementsSidebarIsOpen}
            setDisbursementsSidebarIsOpen={setDisbursementsSidebarIsOpen}
            impoundsSidebarIsOpen={impoundsSidebarIsOpen}
            setImpoundsSidebarIsOpen={setImpoundsSidebarIsOpen}
            feesSidebarIsOpen={feesSidebarIsOpen}
            setFeesSidebarIsOpen={setFeesSidebarIsOpen}
          />
        )
      },
      {
        key: "2",
        label: "Payment History",
        children: (
          <PaymentHistory
            contractId={contractId}
            accountNumber={data ? data.accountNumber : 0}
            key={`PaymentHistory${contractId}`}
            reloadAccount={reloadAccount}
          />
        )
      },
      {
        key: "3",
        label: "Legal",
        children: (
          <Legal
            contractId={contractId}
            primaryBuyerId={data?.primaryBuyerId}
            primarySellerId={data?.hasPrimarySellerId}
            key={`Legal${contractId}`}
            active={active}
            data={legalData}
            isLoading={isLoading}
            reloadAccount={reloadAccount}
          />
        )
      },
      {
        key: "4",
        label: "Documents",
        children: <Documents accountId={contractId} key={`Doc${contractId}`} />
      },
      {
        key: "5",
        label: "Ticklers",
        children: (
          <Ticklers
            accountId={contractId}
            key={`Tickler${contractId}`}
            reloadAccount={reloadAccount}
            sidebarIsOpen={ticklersSidebarIsOpen}
            setSidebarIsOpen={setTicklersSidebarIsOpen}
          />
        )
      },
      {
        key: "6",
        label: "Mailings",
        children: (
          <Mailings
            contractId={contractId}
            key={`Mailings${contractId}${mailingKey}`}
            mailing={data?.mailing}
            reloadAccount={reloadAccount}
          />
        )
      },
      {
        key: "7",
        label: "Payoff Quotes",
        children: (
          <Payoff
            contractId={contractId}
            primaryBuyerId={data?.primaryBuyerId}
            primarySellerId={data?.primarySellerId}
            key={`PayoffQuotes${contractId}`}
          />
        )
      },
      {
        key: "8",
        label: "Incidents",
        children: (
          <IncidentReports id={contractId} type={1} key={`Incidents${contractId}`} entryNumber={contractNumber} />
        )
      }
    ];
  }, [
    contractId,
    form,
    data,
    isLoading,
    isFormDisabled,
    reloadAccount,
    lcSectionKey,
    disbursementsSidebarIsOpen,
    impoundsSidebarIsOpen,
    feesSidebarIsOpen,
    active,
    legalData,
    ticklersSidebarIsOpen,
    mailingKey,
    contractNumber
  ]);

  return (
    <div className="logged-in__page account-view__container">
      {isLoading ? (
        <Loader fontSize="128px" />
      ) : (
        <>
          <Row className="py-5 px-8 align-center title justify-space-between header-container">
            <AccountHeader
              accountNumber={data.accountNumber}
              xRef={data.xRef}
              accountStatus={data.status}
              onBackButtonClicked={onBackButtonClicked}
              reduceBackStep={reduceBackStep}
              enableSearch={true}
            />
            <HeaderActions isLoading={isLoading} data={data} />
          </Row>
          <Row className="account-tabs">
            <Tabs activeKey={active} items={tabItems} onTabClick={onChangeTab} tabBarExtraContent={lastUpdated} />
          </Row>
          {hasChanges && active === "1" && (
            <>
              <EditPanel
                isDisabled={isFormDisabled}
                update={updateAndReset}
                className="edit-panel-container-top"
                onCancelChanges={onCancelChanges}
              />
              <LeaveModal hasChanges={hasChanges} onSave={update} />
            </>
          )}
        </>
      )}
    </div>
  );
};
